import React, { Fragment } from 'react';

import { useIntl } from 'react-intl';

import { isSafeUrl } from '@atlaskit/adf-schema';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	type AnalyticsEventPayload,
	type DispatchAnalyticsEvent,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics';
import { mediaInsertMessages } from '@atlaskit/editor-common/messages';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import Form, {
	ErrorMessage,
	Field,
	FormFooter,
	HelperMessage,
	MessageWrapper,
} from '@atlaskit/form';
import ExpandIcon from '@atlaskit/icon/core/grow-diagonal';
import { getMediaClient } from '@atlaskit/media-client-react';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, Stack, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import TextField from '@atlaskit/textfield';

import type {
	CustomizedHelperMessage,
	InsertExternalMediaSingle,
	InsertMediaSingle,
} from '../types';

import { MediaCard } from './MediaCard';
import type { OnInsertAttrs } from './types';
import { useAnalyticsEvents } from './useAnalyticsEvents';

const PreviewBoxStyles = xcss({
	borderWidth: 'border.width',
	borderStyle: 'dashed',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	height: '200px',
});

const PreviewImageStyles = xcss({
	height: '200px',
});

const FormStyles = xcss({
	flexGrow: 1,
});

type PreviewState = {
	error: string | null;
	isLoading: boolean;
	previewInfo: Required<OnInsertAttrs> | null;
	warning: string | null;
};

type PreviewStateAction =
	| { type: 'loading' }
	| { error: string; type: 'error' }
	| { type: 'warning'; url: string; warning: string }
	| { payload: Required<OnInsertAttrs>; type: 'success' }
	| { type: 'reset' };

const INITIAL_PREVIEW_STATE: Readonly<PreviewState> = Object.freeze({
	isLoading: false,
	error: null,
	warning: null,
	previewInfo: null,
});

const isValidInput = (
	value: string,
	customizedUrlValidation?: (input: string) => boolean,
): boolean => {
	if (customizedUrlValidation) {
		return customizedUrlValidation(value);
	}
	return isValidUrl(value);
};

const MAX_URL_LENGTH = 2048;
export const isValidUrl = (value: string): boolean => {
	try {
		// Check for spaces and length first to avoid the expensive URL parsing
		// Ignored via go/ees005
		// eslint-disable-next-line require-unicode-regexp
		if (/\s/.test(value) || value.length > MAX_URL_LENGTH) {
			return false;
		}
		new URL(value);
	} catch {
		return false;
	}
	return isSafeUrl(value);
};

const previewStateReducer = (state: PreviewState, action: PreviewStateAction) => {
	switch (action.type) {
		case 'loading':
			return { ...INITIAL_PREVIEW_STATE, isLoading: true };
		case 'error':
			return { ...INITIAL_PREVIEW_STATE, error: action.error };
		case 'warning':
			return { ...INITIAL_PREVIEW_STATE, warning: action.warning };
		case 'success':
			return { ...INITIAL_PREVIEW_STATE, previewInfo: action.payload };
		case 'reset':
			return INITIAL_PREVIEW_STATE;
		default:
			return state;
	}
};

type Props = {
	closeMediaInsertPicker: () => void;
	customizedHelperMessage?: CustomizedHelperMessage;
	customizedUrlValidation?: (input: string) => boolean;
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent;
	insertExternalMediaSingle: InsertExternalMediaSingle;
	insertMediaSingle: InsertMediaSingle;
	isOnlyExternalLinks: boolean;
	mediaProvider: MediaProvider;
};

export function MediaFromURL({
	mediaProvider,
	dispatchAnalyticsEvent,
	closeMediaInsertPicker,
	insertMediaSingle,
	insertExternalMediaSingle,
	isOnlyExternalLinks,
	customizedUrlValidation,
	customizedHelperMessage,
}: Props): React.JSX.Element {
	const intl = useIntl();
	const strings = {
		loadPreview: intl.formatMessage(mediaInsertMessages.loadPreview),
		insert: intl.formatMessage(mediaInsertMessages.insert),
		pasteLinkToUpload: intl.formatMessage(mediaInsertMessages.pasteLinkToUpload),
		cancel: intl.formatMessage(mediaInsertMessages.cancel),
		errorMessage: intl.formatMessage(mediaInsertMessages.fromUrlErrorMessage),
		warning: intl.formatMessage(mediaInsertMessages.fromUrlWarning),
		invalidUrl: intl.formatMessage(mediaInsertMessages.invalidUrlErrorMessage),
	};

	const [previewState, dispatch] = React.useReducer(previewStateReducer, INITIAL_PREVIEW_STATE);
	const [input, setInput] = React.useState('');
	const pasteFlag = React.useRef(false);

	const {
		onUploadButtonClickedAnalytics,
		onUploadCommencedAnalytics,
		onUploadSuccessAnalytics,
		onUploadFailureAnalytics,
	} = useAnalyticsEvents(dispatchAnalyticsEvent);

	const uploadExternalMedia = React.useCallback(
		async (url: string) => {
			onUploadButtonClickedAnalytics();
			dispatch({ type: 'loading' });
			const { uploadMediaClientConfig, uploadParams } = mediaProvider;
			if (!uploadMediaClientConfig) {
				return;
			}

			const mediaClient = getMediaClient(uploadMediaClientConfig);
			const collection = uploadParams?.collection;

			onUploadCommencedAnalytics('url');
			try {
				const { uploadableFileUpfrontIds, dimensions, mimeType } =
					await mediaClient.file.uploadExternal(url, collection);
				onUploadSuccessAnalytics('url');
				dispatch({
					type: 'success',
					payload: {
						id: uploadableFileUpfrontIds.id,
						collection,
						dimensions,
						occurrenceKey: uploadableFileUpfrontIds.occurrenceKey,
						fileMimeType: mimeType,
					},
					// eslint-disable-next-line no-unused-vars
				});
			} catch (e) {
				// eslint-disable-line no-unused-vars
				if (typeof e === 'string' && e === 'Could not download remote file') {
					// TODO: ED-26962 - Make sure this gets good unit test coverage with the actual media plugin.
					// This hard coded error message could be changed at any
					// point and we need a unit test to break to stop people changing it.
					onUploadFailureAnalytics(e, 'url');
					dispatch({ type: 'warning', warning: e, url });
				} else if (e instanceof Error) {
					const message = 'Image preview fetch failed';
					onUploadFailureAnalytics(message, 'url');
					dispatch({ type: 'error', error: message });
				} else {
					onUploadFailureAnalytics('Unknown error', 'url');
					dispatch({ type: 'error', error: 'Unknown error' });
				}
			}
		},
		[
			onUploadButtonClickedAnalytics,
			mediaProvider,
			onUploadCommencedAnalytics,
			onUploadSuccessAnalytics,
			onUploadFailureAnalytics,
		],
	);

	const onURLChange = React.useCallback(
		(e: React.FormEvent<HTMLInputElement>) => {
			const url = e.currentTarget.value;
			dispatch({ type: 'reset' });
			setInput(url);
			if (!isValidInput(url, customizedUrlValidation)) {
				return;
			}
			if (pasteFlag.current) {
				pasteFlag.current = false;
				if (!isOnlyExternalLinks) {
					uploadExternalMedia(url);
				}
			}
		},
		[uploadExternalMedia, isOnlyExternalLinks, customizedUrlValidation],
	);

	const onPaste = React.useCallback(
		(e: React.ClipboardEvent<HTMLInputElement>, inputUrl: string) => {
			// Note: this is a little weird, but the paste event will always be
			// fired before the change event when pasting. We don't really want to
			// duplicate logic by handling pastes separately to changes, so we're
			// just noting paste occurred to then be handled in the onURLChange fn
			// above. The one exception to this is where paste inputs exactly what was
			// already in the input, in which case we want to ignore it.
			if (e.clipboardData.getData('text') !== inputUrl) {
				pasteFlag.current = true;
			}
		},
		[],
	);

	const onInsert = React.useCallback(() => {
		if (previewState.previewInfo) {
			insertMediaSingle({
				mediaState: previewState.previewInfo,
				inputMethod: INPUT_METHOD.MEDIA_PICKER,
			});
		}
		closeMediaInsertPicker();
	}, [closeMediaInsertPicker, insertMediaSingle, previewState.previewInfo]);

	const onExternalInsert = React.useCallback(
		(url: string) => {
			if (previewState.warning || isOnlyExternalLinks) {
				insertExternalMediaSingle({
					url,
					alt: '',
					inputMethod: INPUT_METHOD.MEDIA_PICKER,
				});
			}
			closeMediaInsertPicker();
		},
		[closeMediaInsertPicker, insertExternalMediaSingle, previewState.warning, isOnlyExternalLinks],
	);

	const onInputKeyPress = React.useCallback(
		(event: React.KeyboardEvent<HTMLInputElement> | undefined) => {
			if (event && event.key === 'Esc') {
				if (dispatchAnalyticsEvent) {
					const payload: AnalyticsEventPayload = {
						action: ACTION.CLOSED,
						actionSubject: ACTION_SUBJECT.PICKER,
						actionSubjectId: ACTION_SUBJECT_ID.PICKER_MEDIA,
						eventType: EVENT_TYPE.UI,
						attributes: { exitMethod: INPUT_METHOD.KEYBOARD },
					};
					dispatchAnalyticsEvent(payload);
				}
				closeMediaInsertPicker();
			}
		},
		[dispatchAnalyticsEvent, closeMediaInsertPicker],
	);

	const onCancel = React.useCallback(() => {
		if (dispatchAnalyticsEvent) {
			const payload: AnalyticsEventPayload = {
				action: ACTION.CANCELLED,
				actionSubject: ACTION_SUBJECT.PICKER,
				actionSubjectId: ACTION_SUBJECT_ID.PICKER_MEDIA,
				eventType: EVENT_TYPE.UI,
			};
			dispatchAnalyticsEvent(payload);
		}
		closeMediaInsertPicker();
	}, [closeMediaInsertPicker, dispatchAnalyticsEvent]);

	return (
		<Form<{ inputUrl: string }>
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onSubmit={({ inputUrl }, form) => {
				// This can be triggered from an enter key event on the input even when
				// the button is disabled, so we explicitly do nothing when in loading
				// state.
				if (previewState.isLoading || form.getState().invalid) {
					return;
				}

				if (previewState.previewInfo) {
					return onInsert();
				}

				if (previewState.warning || isOnlyExternalLinks) {
					return onExternalInsert(inputUrl);
				}

				return uploadExternalMedia(inputUrl);
			}}
		>
			{({ formProps }) => (
				// Ignored via go/ees005
				// eslint-disable-next-line react/jsx-props-no-spreading
				<Box xcss={FormStyles}>
					<Stack space="space.150" grow="fill">
						<Field
							isRequired={true}
							name="inputUrl"
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							validate={(value) =>
								value && isValidInput(value, customizedUrlValidation)
									? undefined
									: strings.invalidUrl
							}
						>
							{({ fieldProps: { value, onChange, ...rest }, error, meta }) => (
								<Stack space="space.150" grow="fill">
									<Box>
										<TextField
											// Ignored via go/ees005
											// eslint-disable-next-line react/jsx-props-no-spreading
											{...rest}
											value={value}
											aria-label={
												strings.pasteLinkToUpload
											}
											placeholder={strings.pasteLinkToUpload}
											maxLength={MAX_URL_LENGTH}
											onKeyPress={onInputKeyPress}
											// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
											onPaste={(event) => onPaste(event, value)}
											// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
											onChange={(value) => {
												onURLChange(value);
												onChange(value);
											}}
											// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													e.preventDefault();
													formProps.onSubmit();
												}
											}}
										/>
										{customizedHelperMessage && (
											<Fragment>
												<HelperMessage>{customizedHelperMessage}</HelperMessage>
											</Fragment>
										)}
										<MessageWrapper>
											{error && (
												<ErrorMessage>
													<Box as="span">{error}</Box>
												</ErrorMessage>
											)}
										</MessageWrapper>
									</Box>
									{!previewState.previewInfo &&
										!previewState.error &&
										!previewState.warning &&
										!isOnlyExternalLinks && (
											<Flex xcss={PreviewBoxStyles} alignItems="center" justifyContent="center">
												<Button
													type="button"
													// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
													onClick={() => formProps.onSubmit()}
													isLoading={previewState.isLoading}
													isDisabled={!!error || !meta.dirty}
													// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
													iconBefore={() => <ExpandIcon label="" />}
												>
													{strings.loadPreview}
												</Button>
											</Flex>
										)}
								</Stack>
							)}
						</Field>
						{previewState.previewInfo && (
							<Inline
								alignInline="center"
								alignBlock="center"
								xcss={PreviewImageStyles}
								space="space.200"
							>
								<MediaCard attrs={previewState.previewInfo} mediaProvider={mediaProvider} />
							</Inline>
						)}
						<MessageWrapper>
							{previewState.error && (
								<SectionMessage appearance="error">{strings.errorMessage}</SectionMessage>
							)}
							{previewState.warning && (
								<SectionMessage appearance="warning">{strings.warning}</SectionMessage>
							)}
						</MessageWrapper>
						<FormFooter>
							<ButtonGroup>
								<Button appearance="subtle" onClick={onCancel}>
									{strings.cancel}
								</Button>
								<Button
									type="button"
									appearance="primary"
									isDisabled={
										isOnlyExternalLinks
											? !input || !isValidInput(input, customizedUrlValidation)
											: !previewState.previewInfo && !previewState.warning
									}
									// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
									onClick={() => formProps.onSubmit()}
								>
									{strings.insert}
								</Button>
							</ButtonGroup>
						</FormFooter>
					</Stack>
				</Box>
			)}
		</Form>
	);
}
